import { createContext, ReactNode, useContext, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { revokeInvestmentWorkflow } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service.ts';
import { useNavigate } from 'react-router-dom';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useInvestmentsTable } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/hooks/useInvestmentsTable.ts';

const useInvestmentDetailsController = () => {
	const tMipManage = useTranslationMipManage();
	const { orderedInvestmentIds } = useInvestmentsTable({ forDetailsNavigation: true });
	const navigate = useNavigate();
	const [revokeDrawerOpen, setRevokeDrawerOpen] = useState(false);

	const revokeTsaMutation = useMutation({
		mutationFn: revokeInvestmentWorkflow,

		onSuccess: () => {
			showToastV2({
				title: tMipManage('investment_details.revoke.success'),
				state: 'success',
			});
			navigate('../');
		},
	});

	return {
		orderedInvestmentIds,
		revokeTsaMutation,
		revokeDrawerOpen,
		setRevokeDrawerOpen,
	};
};

type InvestmentDetailsContextValue = ReturnType<typeof useInvestmentDetailsController>;

const InvestmentDetailsContext = createContext<InvestmentDetailsContextValue | null>(null);

export const InvestmentDetailsProvider = ({ children }: { children: ReactNode }) => {
	const value = useInvestmentDetailsController();

	return (
		<InvestmentDetailsContext.Provider value={value}>
			{children}
		</InvestmentDetailsContext.Provider>
	);
};

export const useInvestmentDetails = () => {
	const ctx = useContext(InvestmentDetailsContext);

	if (!ctx) {
		throw new Error('useInvestmentDetails must be used within an InvestmentDetailsProvider');
	}

	return ctx;
};
